import React from "react";
import { 
    IonButton, 
    IonButtons, 
    IonCol, 
    IonContent, 
    IonHeader, 
    IonIcon, 
    IonModal, 
    IonRow, 
    IonTitle, 
    IonToolbar
} from "@ionic/react";
import { close } from "ionicons/icons";
import ErrorAlert from "../Errors/ErrorAlert";
import Spinner from "../Spinners/Spinner";

export interface ModalButton {
    text: string;
    loadingText?: string;
    color?: "primary" | "secondary" | "tertiary" | "light" | "success" | "danger" | "warning";
    onClick: () => void;
    disabled?: boolean;
    
}

export interface BasicModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    children?: React.ReactNode;
    isLoading?: boolean;
    footerButtons?: ModalButton[];
    errorMessage?: string;
    className?: string;
    id?: string;
}

const BasicModal: React.FC<BasicModalProps> = ({ 
    isOpen, 
    onClose, 
    title, 
    children, 
    isLoading = false,
    footerButtons,
    errorMessage,
    className = "",
    id
}) => {
    const buttons = footerButtons || [];

    return (
        <IonModal 
            backdropDismiss={false} 
            isOpen={isOpen}
            cssClass={className}
            mode="md"
            id={id}
        >
            <IonHeader>
                <IonToolbar color="light">
                    <IonTitle className="px-5 ion-text-center font-weight-bold">
                        {title}
                    </IonTitle>
                    <IonButtons slot="end">
                        <IonButton 
                            fill="clear" 
                            onClick={onClose} 
                            disabled={isLoading}
                        >
                            <IonIcon icon={close} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            {/* If Error Message is included, it will be in a sticky header that always shows */}
            {errorMessage && (
                <ErrorAlert error={errorMessage} width="12" />
            )}
            
            {/* Content will be scrollable if larger than the Modal */}
            <IonContent className="ion-padding">
                {children}
            </IonContent>

            {isLoading && (
                <IonRow className="ion-justify-content-center">
                    <IonCol size="12" className="ion-text-center">
                        <Spinner />
                    </IonCol>
                </IonRow>
            )}
            
            {/* If Buttons are included, they will be in a sticky footer that always shows */}
            {buttons.length > 0 && (
                <IonRow 
                    className="ion-justify-content-center bg-light"
                    style={{
                        position: "sticky",
                        bottom: "0",
                        left: "0",
                        right: "0",
                        padding: "0.5rem",
                        borderTop: "1px solid #e5e7eb",
                        backgroundColor: "#f9fafb",
                        zIndex: 1000,
                    }}
                >
                    {buttons.map((button, index) => (
                        <IonCol 
                            key={index} 
                            size={buttons.length < 3 ? "6" : buttons.length == 3 ? "4" : "3"}
                            className="ion-text-center"
                        >
                            <IonButton
                                expand="block"
                                color={button.color || "primary"}
                                fill="solid"
                                size="default"
                                onClick={button.onClick}
                                disabled={button.disabled || isLoading}
                                className="text-uppercase ion-text-wrap"
                            >
                                {isLoading && button.loadingText 
                                    ? button.loadingText 
                                    : button.text
                                }
                            </IonButton>
                        </IonCol>
                    ))}
                </IonRow>
            )}
        </IonModal>
    );
};

export default BasicModal;